import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronDown, Calculator, ShieldCheck, AlertCircle, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { 
  fetchPFInterestCalculation, 
  fetchPFInterestDefaults,
  calculatePFInterest, 
  finalizePFInterest 
} from "../../services/pfInterestApi";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Generate a list of financial years from 2025-26 onwards
 */
function getFinancialYears() {
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const years = [];
  for (let y = startYear; y <= currentYear + 2; y++) {
    years.push(`${y}-${(y + 1).toString().slice(-2)}`);
  }
  return years;
}

/**
 * Get current financial year string (April - March).
 */
function getCurrentFY() {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed (0=Jan, 3=Apr)
  const year = now.getFullYear();
  if (month >= 3) {
    return `${year}-${(year + 1).toString().slice(-2)}`;
  } else {
    return `${year - 1}-${year.toString().slice(-2)}`;
  }
}

// Utility to round and format currency to nearest integer (unit place)
function formatCurrency(val) {
  const parsed = parseFloat(val);
  if (isNaN(parsed)) return "₹0";
  return parsed.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  });
}

export default function PFInterestCalculation() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('pf:view');
  const canAdd = hasPermission('pf:add');

  const [financialYear, setFinancialYear] = useState(getCurrentFY);
  const [loading, setLoading] = useState(false);

  // Statement Metadata
  const [lastYearRate, setLastYearRate] = useState("");

  // Section 1 Inputs
  const [openingBalance, setOpeningBalance] = useState("");
  const [suspenseAmount, setSuspenseAmount] = useState("");
  const [income, setIncome] = useState("");
  const [expenditure, setExpenditure] = useState("");

  // Section 2 Inputs
  const [totalInvestment, setTotalInvestment] = useState("");
  const [cashBookBalance, setCashBookBalance] = useState("");
  const [interestReceived, setInterestReceived] = useState("");
  const [interestReceivable, setInterestReceivable] = useState("");

  // Calculation output state (retrieved from backend upon Calculate/Finalize)
  const [proposedRate, setProposedRate] = useState(null);
  const [proposedMonthlyFactor, setProposedMonthlyFactor] = useState(null);
  const [isCalculated, setIsCalculated] = useState(false);

  // Finalization state
  const [isFinalized, setIsFinalized] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 3000);
  };

  // Load calculations and initialize states from backend APIs
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch current year calculation record if it exists in the database
      const currentCalc = await fetchPFInterestCalculation(financialYear).catch(() => null);

      if (currentCalc) {
        // Load saved overrides and inputs
        setOpeningBalance(String(Math.round(currentCalc.opening_balance || 0)));
        setSuspenseAmount(String(Math.round(currentCalc.suspense_amount || 0)));
        setIncome(String(Math.round(currentCalc.income || 0)));
        setExpenditure(String(Math.round(currentCalc.expenditure || 0)));
        setTotalInvestment(String(Math.round(currentCalc.total_investment || 0)));
        setCashBookBalance(String(Math.round(currentCalc.cash_book_balance || 0)));
        setInterestReceived(String(Math.round(currentCalc.interest_received || 0)));
        setInterestReceivable(String(Math.round(currentCalc.interest_receivable || 0)));
        setLastYearRate(currentCalc.last_year_rate !== null ? String(currentCalc.last_year_rate) : "");
        setProposedRate(currentCalc.final_interest_rate);
        setProposedMonthlyFactor(currentCalc.factor_rate);
        setIsCalculated(true);
        setIsFinalized(currentCalc.status === "finalized");
      } else {
        // 2. No saved calculation exists. Fetch system-derived default values
        const defaults = await fetchPFInterestDefaults(financialYear).catch(() => null);

        if (defaults) {
          setOpeningBalance(String(Math.round(defaults.opening_balance || 0)));
          setSuspenseAmount(String(Math.round(defaults.suspense_amount || 0)));
          setIncome(String(Math.round(defaults.income || 0)));
          setExpenditure(String(Math.round(defaults.expenditure || 0)));
          setTotalInvestment(String(Math.round(defaults.total_investment || 0)));
          setCashBookBalance(String(Math.round(defaults.cash_book_balance || 0)));
          setInterestReceived(String(Math.round(defaults.interest_received || 0)));
          setInterestReceivable(String(Math.round(defaults.interest_receivable || 0)));
          setLastYearRate(defaults.last_year_rate !== null ? String(defaults.last_year_rate) : "");
          setProposedRate(null);
          setProposedMonthlyFactor(null);
          setIsCalculated(false);
          setIsFinalized(false);
        } else {
          // Clear if defaults retrieval fails
          setOpeningBalance("0");
          setSuspenseAmount("0");
          setIncome("0");
          setExpenditure("0");
          setTotalInvestment("0");
          setCashBookBalance("0");
          setInterestReceived("0");
          setInterestReceivable("0");
          setLastYearRate("");
          setProposedRate(null);
          setProposedMonthlyFactor(null);
          setIsCalculated(false);
          setIsFinalized(false);
        }
      }
    } catch (e) {
      console.error("Error loading calculation data:", e);
      showToast("Error loading calculation data", "error");
    } finally {
      setLoading(false);
    }
  }, [financialYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ----------------------------------------------------------------
  // MATHEMATICAL COMPUTATIONS (REAL-TIME CLIENT-SIDE UPDATES FOR EDITING)
  // ----------------------------------------------------------------

  const netOpeningBalance = useMemo(() => {
    const op = Math.round(parseFloat(openingBalance || 0));
    const susp = Math.round(parseFloat(suspenseAmount || 0));
    return op + susp;
  }, [openingBalance, suspenseAmount]);

  const totalBalance = useMemo(() => {
    const inc = Math.round(parseFloat(income || 0));
    return netOpeningBalance + inc;
  }, [netOpeningBalance, income]);

  const closingBalance = useMemo(() => {
    const exp = Math.round(parseFloat(expenditure || 0));
    return totalBalance - exp;
  }, [totalBalance, expenditure]);

  const totalA = useMemo(() => {
    const inv = Math.round(parseFloat(totalInvestment || 0));
    const cb = Math.round(parseFloat(cashBookBalance || 0));
    return inv + cb;
  }, [totalInvestment, cashBookBalance]);

  const totalInterest = useMemo(() => {
    const rec = Math.round(parseFloat(interestReceived || 0));
    const receiv = Math.round(parseFloat(interestReceivable || 0));
    return rec + receiv;
  }, [interestReceived, interestReceivable]);

  const totalB = useMemo(() => {
    return totalInterest + closingBalance;
  }, [totalInterest, closingBalance]);

  const balancesMatch = useMemo(() => {
    return Math.abs(totalA - totalB) < 1 && totalA > 0;
  }, [totalA, totalB]);

  // Year range display strings parsed from the selected financial year
  const yearRange = useMemo(() => {
    const parts = financialYear.split("-");
    if (parts.length === 2) {
      const startYear = parseInt(parts[0]);
      const endYear = startYear + 1;
      return {
        startYear,
        endYear,
        startDisplay: `01-04-${startYear}`,
        endDisplay: `31-03-${endYear}`,
        fullRangeDisplay: `${startYear}-${endYear.toString().slice(-2)}`,
        prevFY: `${startYear - 1}-${startYear.toString().slice(-2)}`
      };
    }
    return {
      startYear: "YYYY",
      endYear: "YYYY",
      startDisplay: "01-04-YYYY",
      endDisplay: "31-03-YYYY",
      fullRangeDisplay: financialYear,
      prevFY: ""
    };
  }, [financialYear]);

  // Handle Calculate API Call
  const handleCalculate = async () => {
    if (closingBalance <= 0) {
      showToast("Closing Balance must be greater than 0 to calculate interest", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        financial_year: financialYear,
        last_year_rate: lastYearRate ? parseFloat(lastYearRate) : null,
        opening_balance: parseFloat(openingBalance || 0),
        suspense_amount: parseFloat(suspenseAmount || 0),
        income: parseFloat(income || 0),
        expenditure: parseFloat(expenditure || 0),
        total_investment: parseFloat(totalInvestment || 0),
        cash_book_balance: parseFloat(cashBookBalance || 0),
        interest_received: parseFloat(interestReceived || 0),
        interest_receivable: parseFloat(interestReceivable || 0)
      };

      const result = await calculatePFInterest(payload);
      
      // Update proposed values
      setProposedRate(result.final_interest_rate);
      setProposedMonthlyFactor(result.factor_rate);
      setIsCalculated(true);

      if (Math.abs(result.total_a - result.total_b) >= 1) {
        showToast("Warning: Investment Total (A) does not match Fund/Interest Total (B). Check inputs.", "error");
      } else {
        showToast("Annual interest rate calculated successfully");
      }
    } catch (e) {
      console.error(e);
      showToast(e.message || "Failed to calculate interest", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle Finalize API Call
  const handleFinalize = async () => {
    if (!isCalculated || proposedRate === null) {
      showToast("Please calculate the interest rate first", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        financial_year: financialYear,
        last_year_rate: lastYearRate ? parseFloat(lastYearRate) : null,
        opening_balance: parseFloat(openingBalance || 0),
        suspense_amount: parseFloat(suspenseAmount || 0),
        income: parseFloat(income || 0),
        expenditure: parseFloat(expenditure || 0),
        total_investment: parseFloat(totalInvestment || 0),
        cash_book_balance: parseFloat(cashBookBalance || 0),
        interest_received: parseFloat(interestReceived || 0),
        interest_receivable: parseFloat(interestReceivable || 0)
      };

      await finalizePFInterest(payload);
      setIsFinalized(true);
      showToast("Annual interest rate finalized for FY " + financialYear);
    } catch (e) {
      console.error(e);
      showToast(e.message || "Failed to finalize interest rate", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!canView) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
        You do not have permission to view PF Interest calculations.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 font-sans text-slate-900 animate-[fadeIn_.15s_ease-out]">
      {/* Header and FY Selector */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wide flex items-center gap-2">
            SUB: INTEREST FOR PROVIDENT FUND
            {loading && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
          </h2>
          <p className="text-xs text-slate-500">Calculate annual interest rate and broadsheet factor rate context</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Financial Year</span>
          <div className="relative">
            <select
              value={financialYear}
              disabled={loading}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="appearance-none rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-3 pr-8 text-sm font-semibold text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {getFinancialYears().map((fy) => (
                <option key={fy} value={fy}>
                  FY {fy} (April - March)
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
          </div>
        </div>
      </div>

      {/* Context Statement */}
      <div className="mb-6 rounded-lg bg-slate-50 p-4 border border-slate-100 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-sm text-slate-700">
            The rate of interest for the last financial year i.e. <strong>FY {yearRange.prevFY}</strong> was
          </span>
          <div className="relative w-24">
            <input
              type="number"
              step="0.01"
              value={lastYearRate}
              disabled={!canAdd || isFinalized || loading}
              onChange={(e) => setLastYearRate(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm font-semibold text-slate-800 focus:border-blue-500 focus:outline-none disabled:opacity-60"
            />
            <span className="absolute right-2 top-1 text-sm text-slate-400">%</span>
          </div>
        </div>
        <p className="text-sm text-slate-700">
          The amount of interest for the financial year <strong>{financialYear}</strong> is as detailed below.
        </p>
      </div>

      {/* Toast Alert */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 animate-[fadeIn_0.3s_ease-out] ${toast.tone === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-semibold">{toast.message}</p>
        </div>
      )}

      {/* Two columns layout matching image sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        
        {/* SECTION 1: BALANCE CALCULATION */}
        <div className="space-y-4 bg-slate-50/30 p-5 rounded-xl border border-slate-100">
          <h3 className="text-sm font-bold text-slate-800 border-b pb-2 uppercase tracking-wide">1. Fund Balance Calculation</h3>
          
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Opening Balance as on {yearRange.startDisplay} (Editable Default)</label>
              <input
                type="number"
                value={openingBalance}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setOpeningBalance(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:bg-slate-100"
              />
            </div>
            
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Suspense Amount (+)</label>
              <input
                type="number"
                value={suspenseAmount}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setSuspenseAmount(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Net Opening Balance (Calculated)</label>
              <input
                type="text"
                readOnly
                value={formatCurrency(netOpeningBalance)}
                className="w-full rounded-lg border border-slate-100 bg-slate-100/50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Income w.e.f. {yearRange.startDisplay} to {yearRange.endDisplay} (+) (Editable Default)</label>
              <input
                type="number"
                value={income}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setIncome(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:bg-slate-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Total (Calculated)</label>
              <input
                type="text"
                readOnly
                value={formatCurrency(totalBalance)}
                className="w-full rounded-lg border border-slate-100 bg-slate-100/50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Less (-) expenditure incurred (Editable Default)</label>
              <input
                type="number"
                value={expenditure}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setExpenditure(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:bg-slate-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700 uppercase tracking-wide">Closing Balance as on {yearRange.endDisplay} (Calculated)</label>
              <input
                type="text"
                readOnly
                value={formatCurrency(closingBalance)}
                className="w-full rounded-lg border border-blue-200 bg-blue-50/30 px-3 py-2 text-sm font-bold text-blue-800 outline-none"
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: FUND VERIFICATION & INTEREST RECEIVED */}
        <div className="space-y-4 bg-slate-50/30 p-5 rounded-xl border border-slate-100">
          <h3 className="text-sm font-bold text-slate-800 border-b pb-2 uppercase tracking-wide">2. Fund & Interest Verification</h3>
          
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Total Investment of Provident Fund as on {yearRange.endDisplay} (Editable Default)</label>
              <input
                type="number"
                value={totalInvestment}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setTotalInvestment(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:bg-slate-100"
              />
            </div>
            
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Cash Book balance upto {yearRange.endDisplay} (+)</label>
              <input
                type="number"
                value={cashBookBalance}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setCashBookBalance(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Total Investment + Cash Book (A) (Calculated)</label>
              <input
                type="text"
                readOnly
                value={formatCurrency(totalA)}
                className="w-full rounded-lg border border-slate-100 bg-slate-100/50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none"
              />
            </div>

            <div className="border-t border-dashed my-3 pt-3">
              <label className="mb-1 block text-xs font-semibold text-slate-600">
                Interest received upto {yearRange.endDisplay} (Editable Default)
              </label>
              <input
                type="number"
                value={interestReceived}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setInterestReceived(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:bg-slate-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Interest receivable upto {yearRange.endDisplay} (+) (Editable Default)</label>
              <input
                type="number"
                value={interestReceivable}
                disabled={!canAdd || isFinalized || loading}
                onChange={(e) => setInterestReceivable(e.target.value)}
                placeholder="₹0"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:bg-slate-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Total Interest for the year {yearRange.fullRangeDisplay} (Calculated)</label>
              <input
                type="text"
                readOnly
                value={formatCurrency(totalInterest)}
                className="w-full rounded-lg border border-slate-100 bg-slate-100/50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Closing Balance (Carried from calculation)</label>
              <input
                type="text"
                readOnly
                value={formatCurrency(closingBalance)}
                className="w-full rounded-lg border border-slate-100 bg-slate-100/50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Total Interest + Closing Balance (B) (Calculated)</label>
              <input
                type="text"
                readOnly
                value={formatCurrency(totalB)}
                className="w-full rounded-lg border border-slate-100 bg-slate-100/50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none"
              />
            </div>
          </div>

          {/* Reconciliation Match Check */}
          {totalA > 0 && totalB > 0 && (
            <div className={`mt-4 flex items-center justify-between rounded-lg px-4 py-2 text-sm font-semibold border ${
              balancesMatch 
                ? "bg-emerald-50 border-emerald-200 text-emerald-800" 
                : "bg-rose-50 border-rose-200 text-rose-800"
            }`}>
              <div className="flex items-center gap-2">
                {balancesMatch ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-rose-600" />}
                <span>
                  {balancesMatch 
                    ? "Mathematical Check: Balances Reconciled!" 
                    : "Balance Sheet Discrepancy detected!"}
                </span>
              </div>
              <span className="font-mono">
                Diff: ₹{Math.abs(totalA - totalB).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 3: CALCULATION OF INTEREST & FINALIZE OPTIONS */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/50 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">
              <Calculator className="h-5 w-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Calculation of Interest</h4>
              <p className="text-xs text-slate-500">Calculate annual interest rate and broadsheet factor based on reconciled figures.</p>
            </div>
          </div>
          {canAdd && (
            <div className="flex gap-3">
              {!isFinalized && (
                <button
                  onClick={handleCalculate}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition shadow-sm disabled:opacity-50"
                >
                  Calculate
                </button>
              )}
              {!isFinalized && (
                <button
                  onClick={handleFinalize}
                  disabled={!isCalculated || !balancesMatch || loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Finalize Rate
                </button>
              )}
              {isFinalized && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                  <ShieldCheck className="h-4 w-4" />
                  Finalized
                </span>
              )}
            </div>
          )}
          {!canAdd && isFinalized && (
            <div className="flex gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                Finalized
              </span>
            </div>
          )}
        </div>

        {/* Math Formulas Rendering matching Image exactly */}
        <div className="mt-6 space-y-6">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Provident Fund Rate Formula:</span>
            <div className="flex flex-wrap items-center gap-3 font-mono text-sm sm:text-base border border-slate-200 rounded-lg p-3 bg-white">
              <div className="flex flex-col items-center">
                <span className="pb-1 border-b border-slate-400">Total Interest Rs. {parseInt(totalInterest).toLocaleString('en-IN')}</span>
                <span>{closingBalance > 0 ? parseInt(closingBalance).toLocaleString('en-IN') : "Closing Balance"}</span>
              </div>
              <span className="text-slate-500 font-bold">X 100</span>
              <span className="text-slate-600">=</span>
              <span className="font-bold text-blue-700 text-lg">
                {proposedRate !== null ? proposedRate.toFixed(2) : "—"}%
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Factor Rate for Broadsheet:</span>
            <div className="flex flex-wrap items-center gap-3 font-mono text-sm sm:text-base border border-slate-200 rounded-lg p-3 bg-white">
              <span className="font-bold text-slate-700">{proposedRate !== null ? proposedRate.toFixed(2) : "—"}%</span>
              <span className="text-slate-400">/ 100 / 12</span>
              <span className="text-slate-600">=</span>
              <span className="font-bold text-blue-700 text-lg">
                {proposedMonthlyFactor !== null ? (proposedMonthlyFactor * 100).toFixed(4) : "—"}%
              </span>
              {proposedMonthlyFactor !== null && (
                <span className="text-xs text-slate-500 font-sans">
                  (Factor: {proposedMonthlyFactor.toFixed(8)})
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
